import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { CompanyAdminLayout } from "../../../components/CompanyAdmin/CompanyAdminLayout";
import { Button } from "../../../components/UI/Button";
import { useAuthStore } from "../../../stores/auth-store";
import { contentUseCase } from "../../../../di/container";
import type { PortfolioLinkedOrder } from "../../../../domain/types/content";
import {
  buildSaveResultMessage,
  resolveErrorMessage,
  runSaveSteps,
} from "../../../../shared/utils/save-flow";
import type { PortfolioButtonDraft } from "./components/PortfolioButtonsEditor";
import { PortfolioOrderPickerModal } from "./components/PortfolioOrderPickerModal";
import {
  buildContentSaveSteps,
  EMPTY_FORM,
  findButtonProblem,
  PortfolioFormFields,
  type PortfolioForm,
} from "./portfolio-form";

/**
 * 포트폴리오 추가 페이지 (`/company/{id}/contents/portfolios/new`).
 *
 * 입력 항목이 많고(마크다운 본문·이미지·버튼·연결 주문) 모달 안 스크롤로는 쓰기 불편해 페이지로 뺐다.
 * 입력 영역과 저장 단계는 목록의 수정 모달과 같은 것을 쓴다(portfolio-form).
 */
const PortfolioCreatePage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const backToList = () => navigate("..", { relative: "path" });

  const [form, setForm] = useState<PortfolioForm>(EMPTY_FORM);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [images, setImages] = useState<File[]>([]);
  const [buttons, setButtons] = useState<PortfolioButtonDraft[]>([]);
  const [orders, setOrders] = useState<PortfolioLinkedOrder[]>([]);
  const [isOrderPickerOpen, setIsOrderPickerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleCreate = async () => {
    if (!user?.companyId || isSaving) return;
    if (form.title.trim() === "") {
      alert("제목을 입력해주세요.");
      return;
    }
    const buttonProblem = findButtonProblem(buttons);
    if (buttonProblem) {
      alert(buttonProblem);
      return;
    }

    const companyId = user.companyId;
    setIsSaving(true);
    try {
      /*
        버튼/주문 연결은 포트폴리오 id 를 알아야 저장할 수 있다.
        그래서 본문 생성이 먼저고, 그 결과의 id 로 나머지 두 단계를 잇는다.
        본문 생성이 실패하면 뒤 단계는 시도조차 하지 않는다.
      */
      const created = await contentUseCase.createPortfolio(
        companyId,
        form,
        thumbnail || undefined,
        images.length > 0 ? images : undefined,
      );
      const result = await runSaveSteps(buildContentSaveSteps(companyId, created.id, buttons, orders));
      if (result.failed) {
        // 포트폴리오 자체는 이미 만들어졌다. 그 사실을 분명히 알린다.
        alert(`포트폴리오는 등록되었습니다. ${buildSaveResultMessage(result)}`);
      }
      backToList();
    } catch (error) {
      alert(resolveErrorMessage(error, "포트폴리오 생성에 실패했습니다."));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {isOrderPickerOpen && user?.companyId ? (
        <PortfolioOrderPickerModal
          isModalVisible
          setIsModalVisible={() => setIsOrderPickerOpen(false)}
          companyId={user.companyId}
          selectedOrders={orders}
          onChange={setOrders}
        />
      ) : null}

      <CompanyAdminLayout>
        <div className="p-5">
          <div className="mb-[28px] flex flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={backToList}
                aria-label="포트폴리오 목록으로"
                className="p-1 -ml-1 rounded-[6px] text-[#555] hover:bg-gray-200"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <h2 className="text-[1.375rem] font-semibold">포트폴리오 추가</h2>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={backToList} disabled={isSaving}>
                취소
              </Button>
              <Button type="button" onClick={handleCreate} loading={isSaving} disabled={isSaving}>
                등록
              </Button>
            </div>
          </div>

          <div className="max-w-[860px] p-6 bg-white border border-[#d9d9d9] rounded-[0.625rem]">
            <PortfolioFormFields
              form={form}
              setForm={setForm}
              thumbnailPreview={thumbnailPreview}
              onThumbnailChange={(file) => {
                setThumbnailPreview(URL.createObjectURL(file));
                setThumbnail(file);
              }}
              selectedImageCount={images.length}
              onImagesChange={setImages}
              imagesHint="여러 장을 한 번에 선택할 수 있습니다."
              buttons={buttons}
              onButtonsChange={setButtons}
              orders={orders}
              onOrdersChange={setOrders}
              onOpenOrderPicker={() => setIsOrderPickerOpen(true)}
            />

            <div className="flex justify-end gap-2 pt-4 mt-2 border-t border-[#eee]">
              <Button type="button" variant="outline" onClick={backToList} disabled={isSaving}>
                취소
              </Button>
              <Button type="button" onClick={handleCreate} loading={isSaving} disabled={isSaving}>
                등록
              </Button>
            </div>
          </div>
        </div>
      </CompanyAdminLayout>
    </>
  );
};

export default PortfolioCreatePage;
